<template>
  <main>
    <!-- Hero + Sección Apps lado a lado -->
    <section aria-labelledby="hero-title">
      <v-row
        class="mt-0"
        align="stretch"
      >
        <!-- Hero Section -->
        <v-col
          cols="12"
          lg="8"
        >
          <v-card
            class="pa-3 pa-md-5 pa-lg-6 rounded-xl hero-card h-100"
            :class="{ 'animations-paused': !animationsEnabled }"
            color="transparent"
            variant="flat"
            elevation="4"
            role="region"
            :aria-label="t('home.aria.hero')"
          >
            <!-- Botón solo visible en desktop sin prefers-reduced-motion -->
            <v-btn
              v-if="showAnimationToggle"
              type="button"
              icon
              size="x-small"
              class="animation-toggle-btn"
              :aria-label="
                animationsEnabled
                  ? t('home.aria.pausar_animaciones')
                  : t('home.aria.reanudar_animaciones')
              "
              :aria-pressed="!animationsEnabled"
              @click="toggleAnimations"
            >
              <v-icon
                size="small"
                aria-hidden="true"
              >
                {{
                  animationsEnabled ? "mdi-pause" : "mdi-play"
                }}
              </v-icon>
            </v-btn>

            <!-- Círculos animados SOLO en desktop -->
            <div
              v-if="showAnimations"
              class="animated-background"
              aria-hidden="true"
            >
              <div
                v-for="n in 8"
                :key="n"
                :class="`circle circle-${n}`"
              />
            </div>

            <!-- Fondo estático para móvil (más ligero) -->
            <div
              v-else
              class="static-background"
              aria-hidden="true"
            />

            <div class="hero-overlay" />

            <div class="hero-content">
              <v-card-item>
                <h1
                  id="hero-title"
                  class="text-h5 text-md-h4 text-lg-h3 text-white hero-title"
                >
                  {{ t("home.titulo") }}
                </h1>
                <p
                  class="mt-2 text-body-2 text-md-body-1 text-white hero-subtitle"
                >
                  {{ t("home.subtitulo") }}
                </p>
              </v-card-item>

              <v-card-item class="mt-2 mt-md-3">
                <v-btn
                  type="button"
                  :size="isMobile ? 'default' : 'large'"
                  color="secondary"
                  class="start-test-btn rounded-pill"
                  :class="{ 'mobile-btn': isMobile }"
                  :block="isMobile"
                  :aria-label="t('home.aria.comenzar_test')"
                  @click="startTestFromHome"
                >
                  <v-icon
                    start
                    aria-hidden="true"
                    :size="isMobile ? 'small' : 'default'"
                  >
                    mdi-speedometer
                  </v-icon>
                  <span class="button-text font-weight-bold">{{
                    t("home.secciones.titulo_testVelocidad")
                  }}</span>
                  <v-icon
                    v-if="!isMobile"
                    end
                    aria-hidden="true"
                  >
                    mdi-chevron-right
                  </v-icon>
                </v-btn>
              </v-card-item>
            </div>
          </v-card>
        </v-col>

        <!-- Sección Apps con QR -->
        <v-col
          cols="12"
          lg="4"
        >
          <v-card
            class="download-card rounded-xl h-100"
            elevation="2"
            role="region"
            :aria-label="t('home.aria.descarga_apps')"
          >
            <v-card-text class="pa-4 d-flex flex-column h-100">
              <div class="text-center mb-3">
                <div class="d-flex align-center justify-center mb-1">
                  <v-icon
                    size="default"
                    color="primary"
                    class="mr-2"
                    aria-hidden="true"
                  >
                    mdi-cellphone
                  </v-icon>
                  <h2
                    id="download-apps-title"
                    class="text-subtitle-1 text-md-h6 font-weight-bold text-primary ma-0"
                  >
                    {{ t("home.descargas.titulo") }}
                  </h2>
                </div>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ t("home.descargas.subtitulo_corto") }}
                </p>
              </div>

              <v-row
                justify="center"
                align="center"
                class="flex-grow-1"
              >
                <v-col
                  v-for="store in visibleStores"
                  :key="store.name"
                  :cols="visibleStores.length === 1 ? 12 : 6"
                  class="text-center"
                >
                  <div class="store-download-item">
                    <div class="qr-wrapper">
                      <img
                        :src="store.qr"
                        :alt="t(store.qrAlt)"
                        class="qr-image"
                        width="100"
                        height="100"
                        loading="lazy"
                      >
                    </div>
                    <a
                      :href="store.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      :class="['store-badge-download', store.badgeClass]"
                      :aria-label="t(store.ariaLabel)"
                    >
                      <img
                        :src="store.badge"
                        :alt="t(store.badgeAlt)"
                        class="badge-image"
                        :width="store.name === 'googlePlay' ? 130 : 120"
                        :height="store.name === 'googlePlay' ? 50 : 40"
                        loading="lazy"
                      >
                    </a>
                  </div>
                </v-col>
              </v-row>

              <div class="text-center mt-3">
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ t("home.descargas.instruccion_corta") }}
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <!-- Franja de estadísticas -->
    <section
      ref="statsStripRef"
      class="stats-hero"
    >
      <div class="stats-hero-bg" />
      <!-- Skeleton placeholders while loading -->
      <div
        v-if="!homeStats.totalTests"
        class="stats-hero-content"
      >
        <div class="stat-item">
          <span class="stat-skeleton stat-skeleton--number" />
          <span class="stat-skeleton stat-skeleton--label" />
        </div>
        <div class="stat-dot" />
        <div class="stat-item">
          <span class="stat-skeleton stat-skeleton--number" />
          <span class="stat-skeleton stat-skeleton--label" />
        </div>
        <div class="stat-dot" />
        <div class="stat-item">
          <span class="stat-skeleton stat-skeleton--number" />
          <span class="stat-skeleton stat-skeleton--label" />
        </div>
      </div>
      <!-- Actual stats content -->
      <div
        v-else
        class="stats-hero-content"
      >
        <div class="stat-item">
          <span class="stat-big-number">{{ formatNumber(animatedTests || homeStats.totalTests) }}</span>
          <span class="stat-big-label">{{ t('home.stats.tests_realizados') }}</span>
          <span
            v-if="homeStats.spainPct"
            class="stat-sub"
          >{{ homeStats.spainPct }} % {{ t('home.stats.espana') }} · {{ 100 - homeStats.spainPct }} % {{ t('home.stats.resto') }}</span>
        </div>
        <div class="stat-dot" />
        <div class="stat-item">
          <span class="stat-big-number">{{ animatedPoblacion || homeStats.poblacionPct }}<span class="stat-big-pct"> %</span></span>
          <span class="stat-big-label">{{ t('home.stats.poblacion_cubierta') }}</span>
        </div>
        <div class="stat-dot" />
        <div class="stat-item">
          <span class="stat-big-number">{{ formatNumber(animatedMunicipios || homeStats.municipiosConDatos) }}</span>
          <span class="stat-big-label">{{ t('home.stats.municipios') }}</span>
        </div>
      </div>
    </section>

    <!-- Secciones principales -->
    <section aria-labelledby="sections-title">
      <h2
        id="sections-title"
        class="sr-only"
      >
        {{ t("home.aria.secciones_principales") }}
      </h2>
      <v-row class="mt-n1 mb-2 my-md-3">
        <v-col
          v-for="(section, index) in sections"
          :key="index"
          cols="12"
          sm="6"
          lg="3"
        >
          <v-card
            class="section-card rounded-xl"
            tabindex="0"
            role="button"
            :aria-label="`${t('home.aria.ir_a')} ${t(section.title)}: ${t(
              section.subtitle
            )}`"
            elevation="0"
            @click="navigateToSection(section)"
            @keydown.enter="navigateToSection(section)"
            @keydown.space.prevent="navigateToSection(section)"
          >
            <div class="section-card-gradient" />
            <div class="section-card-content">
              <v-card-item class="pa-3 pa-md-4">
                <div class="d-flex align-center mb-2">
                  <div class="section-icon-wrapper">
                    <v-icon
                      :icon="section.icon"
                      size="large"
                      class="section-icon"
                      aria-hidden="true"
                    />
                  </div>
                  <v-card-title
                    class="text-h6 text-md-h5 ml-2 pa-0 section-title"
                  >
                    {{ t(section.title) }}
                  </v-card-title>
                </div>
              </v-card-item>
              <v-card-item class="pa-3 pa-md-4 pt-0">
                <div
                  class="d-flex align-end justify-space-between"
                  style="gap: 12px;"
                >
                  <p
                    class="text-body-2 text-md-body-1 section-subtitle"
                    style="margin: 0;"
                  >
                    {{ t(section.subtitle) }}
                  </p>
                  <div class="section-arrow">
                    <v-icon
                      size="default"
                      aria-hidden="true"
                    >
                      mdi-arrow-right
                    </v-icon>
                  </div>
                </div>
              </v-card-item>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <!-- Modal para dispositivos móviles -->
    <v-dialog
      v-model="showMobileDialog"
      max-width="500"
      persistent
      :aria-labelledby="'mobile-dialog-title'"
    >
      <v-card
        class="mobile-dialog-qr rounded-xl"
        role="dialog"
        aria-modal="true"
      >
        <v-card-text class="pa-5 pa-md-6 text-center">
          <div class="mb-3 logo-wrapper-modal">
            <img
              :src="logoCNMC"
              alt="Logo CNMC"
              class="cnmc-logo-modal"
              width="200"
              height="22"
              loading="lazy"
            >
          </div>

          <h2
            id="mobile-dialog-title"
            class="text-h6 font-weight-bold mb-4"
          >
            {{ t("home.modal.titulo_simple") }}
          </h2>
          <v-divider
            class="mb-4"
            role="separator"
          />

          <div class="qr-modal-section mb-4">
            <div class="qr-modal-wrapper">
              <img
                :src="currentStoreQR"
                :alt="currentStoreQRAlt"
                class="qr-modal-image"
                width="160"
                height="160"
                loading="lazy"
              >
            </div>
            <p class="text-caption text-medium-emphasis mt-2">
              {{ t("home.modal.escanear_qr") }}
            </p>
          </div>

          <div class="modal-badge-single mb-4">
            <a
              :href="currentStoreURL"
              target="_blank"
              rel="noopener noreferrer"
              class="modal-store-badge"
              :aria-label="currentStoreAriaLabel"
            >
              <img
                :src="currentStoreBadge"
                :alt="currentStoreBadgeAlt"
                class="modal-badge-img"
                :width="isIOS ? 120 : 135"
                height="50"
                loading="lazy"
              >
            </a>
          </div>

          <v-divider
            class="my-4"
            role="separator"
          />

          <v-btn
            type="button"
            variant="text"
            size="default"
            class="text-none"
            block
            :aria-label="t('home.modal.continuar_web')"
            @click="continueInBrowser"
          >
            <v-icon
              start
              size="small"
              aria-hidden="true"
            >
              mdi-web
            </v-icon>
            <span class="text-medium-emphasis">{{
              t("home.modal.continuar_web")
            }}</span>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </main>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { getLocaleFromRoute } from "@/router";

import googlePlayES from "@/assets/badges/google-play-es.png";
import googlePlayEN from "@/assets/badges/google-play-en.png";
import appStoreES from "@/assets/badges/app-store-es.svg";
import appStoreEN from "@/assets/badges/app-store-en.svg";
import logoCNMC from "@/assets/CNMC_Logotipo.png";
import qrGooglePlay from "@/assets/qr/qr-google-play.png";
import qrAppStore from "@/assets/qr/qr-app-store.png";
import { openTestServices } from "@/services/openTest.services";


const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();

// ============================================
// Estado
// ============================================
const prefersReducedMotion = ref(false);
const animationsEnabled = ref(true);
const showMobileDialog = ref(false);
const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 960 : false);
const isIOS = ref(typeof navigator !== 'undefined' ? /iPad|iPhone|iPod/.test(navigator.userAgent) : false);

// Home stats
const homeStats = ref({});
const statsStripRef = ref(null);
const animatedTests = ref(0);
const animatedPoblacion = ref(0);
const animatedMunicipios = ref(0);
let statsAnimated = false;

const formatNumber = (n) => {
  if (!n) return "0";
  return new Intl.NumberFormat("es-ES").format(n);
};

function animateCounter(refVal, target, duration = 1500) {
  const start = performance.now();
  const step = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - (1 - progress) ** 3; // ease-out cubic
    refVal.value = Math.round(eased * target);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function startCounters() {
  if (statsAnimated) return;
  statsAnimated = true;
  animateCounter(animatedTests, homeStats.value.totalTests, 1800);
  animateCounter(animatedPoblacion, homeStats.value.poblacionPct, 1200);
  animateCounter(animatedMunicipios, homeStats.value.municipiosConDatos, 1500);
}


const loadHomeStats = async () => {
  const data = await openTestServices.getHomeStats();
  if (data.totalTests) {
    homeStats.value = {
      ...data,
      poblacionPct: data.poblacionCubierta && data.totalHabitantesEspana
        ? Math.round((100 * data.poblacionCubierta) / data.totalHabitantesEspana)
        : 0,
      spainPct: data.totalTestsSpain && data.totalTests
        ? Math.round((100 * data.totalTestsSpain) / data.totalTests)
        : null,
    };
  }
};


// ============================================
// Computed para animaciones
// ============================================

// Mostrar animaciones SOLO en desktop Y sin prefers-reduced-motion
const showAnimations = computed(() => {
  return (
    !isMobile.value && !prefersReducedMotion.value && animationsEnabled.value
  );
});

// Mostrar botón toggle SOLO en desktop sin prefers-reduced-motion
const showAnimationToggle = computed(() => {
  return !isMobile.value && !prefersReducedMotion.value;
});

// Obtener locale actual
const currentLocale = computed(() => getLocaleFromRoute(route));

// Helper para navegar manteniendo el idioma
const navigateTo = (baseName, query = {}) => {
  const routeName = currentLocale.value === "en" ? `${baseName}-en` : baseName;
  router.push({ name: routeName, query }).then(() => {
    window.scrollTo({ top: 0 });
  });
};

const sections = [
  {
    icon: "mdi-history",
    title: "home.secciones.titulo_historial",
    subtitle: "home.secciones.subtitulo_historial",
    routeName: "historial",
  },
  {
    icon: "mdi-chart-bar",
    title: "home.secciones.titulo_estadisticas",
    subtitle: "home.secciones.subtitulo_estadisticas",
    routeName: "estadisticas",
  },
  {
    icon: "mdi-map-marker-radius",
    title: "home.secciones.titulo_mapa",
    subtitle: "home.secciones.subtitulo_mapa",
    routeName: "mapa",
  },
  {
    icon: "mdi-database-search",
    title: "home.secciones.titulo_datosAbiertos",
    subtitle: "home.secciones.subtitulo_datosAbiertos",
    routeName: "datos",
  },
];

const googlePlayBadgeUrl = computed(() =>
  locale.value === "es" ? googlePlayES : googlePlayEN
);
const appStoreBadgeUrl = computed(() =>
  locale.value === "es" ? appStoreES : appStoreEN
);

const stores = computed(() => [
  {
    name: "googlePlay",
    qr: qrGooglePlay,
    qrAlt: "home.descargas.qrGooglePlay",
    url: "https://play.google.com/store/apps/details?id=es.gob.cnmc&hl=es",
    badge: googlePlayBadgeUrl.value,
    badgeClass: "google-play-badge",
    ariaLabel: "home.descargarGooglePlay",
    badgeAlt: "home.disponibleGooglePlay",
  },
  {
    name: "appStore",
    qr: qrAppStore,
    qrAlt: "home.descargas.qrAppStore",
    url: "https://apps.apple.com/us/app/test-velocidad/id6476399770",
    badge: appStoreBadgeUrl.value,
    badgeClass: "app-store-badge",
    ariaLabel: "home.descargarAppStore",
    badgeAlt: "home.disponibleAppStore",
  },
]);

const visibleStores = computed(() => {
  if (!isMobile.value) return stores.value;
  return isIOS.value
    ? stores.value.filter((s) => s.name === "appStore")
    : stores.value.filter((s) => s.name === "googlePlay");
});

const currentStoreQR = computed(() =>
  isIOS.value ? qrAppStore : qrGooglePlay
);
const currentStoreQRAlt = computed(() =>
  t(isIOS.value ? "home.descargas.qrAppStore" : "home.descargas.qrGooglePlay")
);
const currentStoreURL = computed(() =>
  isIOS.value ? stores.value[1].url : stores.value[0].url
);
const currentStoreBadge = computed(() =>
  isIOS.value ? appStoreBadgeUrl.value : googlePlayBadgeUrl.value
);
const currentStoreBadgeAlt = computed(() =>
  t(isIOS.value ? "home.disponibleAppStore" : "home.disponibleGooglePlay")
);
const currentStoreAriaLabel = computed(() =>
  t(isIOS.value ? "home.descargarAppStore" : "home.descargarGooglePlay")
);

// ============================================
// Detectar preferencias del sistema
// ============================================
const checkIfMobile = () => {
  isMobile.value = window.innerWidth < 960;
};

// Detectar prefers-reduced-motion del sistema
const checkReducedMotion = () => {
  if (typeof window === "undefined") return;

  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  prefersReducedMotion.value = mediaQuery.matches;

  // Si el usuario prefiere movimiento reducido, desactivar animaciones
  if (mediaQuery.matches) {
    animationsEnabled.value = false;
  }
};

// Listener para cambios dinámicos en prefers-reduced-motion
let motionMediaQuery = null;
const handleMotionChange = (e) => {
  prefersReducedMotion.value = e.matches;
  if (e.matches) {
    animationsEnabled.value = false;
  } else {
    // Si el usuario quita la preferencia, reactivar animaciones
    animationsEnabled.value = true;
  }
};

// Toggle manual de animaciones (solo visible en desktop sin prefers-reduced-motion)
const toggleAnimations = () => {
  animationsEnabled.value = !animationsEnabled.value;
};


// Observar cuando la franja de contadores sea visible
let statsObserver = null;

watch(
  () => homeStats.value.totalTests,
  async (val) => {
    if (!val) return;
    await nextTick();
    if (!statsStripRef.value) return;
    statsObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          startCounters();
          statsObserver.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    statsObserver.observe(statsStripRef.value);
  }
);

onMounted(() => {
  // Detectar móvil
  checkIfMobile();
  window.addEventListener("resize", checkIfMobile);

  // Detectar iOS
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  isIOS.value = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

  // Detectar preferencia de movimiento reducido
  checkReducedMotion();

  // Cargar estadísticas para contadores
  loadHomeStats();

  // Escuchar cambios en la preferencia (por si el usuario cambia en tiempo real)
  if (typeof window !== "undefined") {
    motionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    motionMediaQuery.addEventListener("change", handleMotionChange);
  }

  // Precargar chunks de rutas pesadas cuando el navegador esté idle
  const prefetch = () => {
    import("@/pages/ViewMap/MapV2");
    import("@/pages/Statistics/Statistics");
  };
  if ("requestIdleCallback" in window) {
    requestIdleCallback(prefetch, { timeout: 3000 });
  } else {
    setTimeout(prefetch, 2000);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", checkIfMobile);
  if (motionMediaQuery) {
    motionMediaQuery.removeEventListener("change", handleMotionChange);
  }
  if (statsObserver) statsObserver.disconnect();
});

const startTestFromHome = () => {
  if (isMobile.value) {
    showMobileDialog.value = true;
  } else {
    navigateTo("test", { autoStart: "true" });
  }
};

const continueInBrowser = () => {
  showMobileDialog.value = false;
  navigateTo("test", { autoStart: "true" });
};

const navigateToSection = (section) => {
  navigateTo(section.routeName);
};

</script>

<style scoped>
/* .sr-only está definido en variables.css global */

/* ============================================
   PAGE STRUCTURE
   ============================================ */
main {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
  padding-bottom: 24px;
}

main > section {
  flex-shrink: 0;
}

main > section:last-of-type {
  flex: 0 0 auto;
}

/* ============================================
   HERO CARD
   ============================================ */
.hero-card {
  background: linear-gradient(135deg, #0a1929 0%, #001e3c 50%, #004d5c 100%);
  position: relative;
  overflow: hidden;
  /* CLS fix: usar clamp() para transición fluida en lugar de breakpoints */
  min-height: clamp(180px, 18vw + 100px, 200px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  contain: layout style;
}

.hero-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 30, 40, 0.2) 100%
  );
  z-index: 2;
}

.hero-overlay,
.hero-content {
  position: relative;
  z-index: 2;
}

.hero-title {
  font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  font-size: 1.75rem !important;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  font-weight: 400;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
  max-width: 700px;
}

/* ============================================
   ANIMATION TOGGLE BUTTON
   ============================================ */
.animation-toggle-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5) !important;
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.animation-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.7) !important;
  border-color: rgba(0, 255, 255, 0.5);
  color: rgba(0, 255, 255, 1) !important;
  transform: scale(1.1);
}

.animation-toggle-btn:focus-visible {
  outline: 2px solid rgba(0, 255, 255, 0.8);
  outline-offset: 2px;
}

/* ============================================
   ANIMATED CIRCLES - SOLO DESKTOP
   ============================================ */
.animated-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 1;
  pointer-events: none;
  contain: strict;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(0, 255, 255, 1) 0%,
    rgba(0, 230, 240, 0.8) 30%,
    rgba(0, 200, 220, 0.5) 60%,
    transparent 100%
  );
  filter: blur(2px);
  box-shadow: 0 0 30px rgba(0, 255, 255, 0.6), 0 0 60px rgba(0, 255, 255, 0.3);
  will-change: transform, opacity;
  transform: translateZ(0);
}

.circle-1 {
  width: 150px;
  height: 150px;
  top: 5%;
  right: 5%;
  animation: floatA 12s ease-in-out infinite;
}
.circle-2 {
  width: 100px;
  height: 100px;
  top: 18%;
  right: 15%;
  animation: floatB 10s ease-in-out infinite -3s;
}
.circle-3 {
  width: 80px;
  height: 80px;
  top: 8%;
  right: 28%;
  animation: floatC 14s ease-in-out infinite -7s;
}
.circle-4 {
  width: 170px;
  height: 170px;
  bottom: 3%;
  right: 2%;
  animation: floatA 16s ease-in-out infinite -2s;
}
.circle-5 {
  width: 120px;
  height: 120px;
  top: 32%;
  right: 10%;
  animation: floatB 11s ease-in-out infinite -5s;
}
.circle-6 {
  width: 90px;
  height: 90px;
  top: 6%;
  right: 38%;
  animation: floatC 13s ease-in-out infinite -8s;
}
.circle-7 {
  width: 130px;
  height: 130px;
  bottom: 12%;
  right: 18%;
  animation: floatA 15s ease-in-out infinite -10s;
}
.circle-8 {
  width: 100px;
  height: 100px;
  top: 42%;
  right: 6%;
  animation: floatB 9s ease-in-out infinite -4s;
}

/* Pausar animaciones via JS */
.animations-paused .circle {
  animation-play-state: paused !important;
}

/* Animaciones usando solo transform (GPU) */
@keyframes floatA {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 0.9;
  }
  25% {
    transform: translate3d(-80px, 90px, 0) scale(1.4);
    opacity: 1;
  }
  50% {
    transform: translate3d(-140px, -70px, 0) scale(0.9);
    opacity: 0.8;
  }
  75% {
    transform: translate3d(-60px, -100px, 0) scale(1.2);
    opacity: 0.95;
  }
}

@keyframes floatB {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 0.95;
  }
  33% {
    transform: translate3d(100px, -80px, 0) scale(1.5);
    opacity: 1;
  }
  66% {
    transform: translate3d(-90px, 70px, 0) scale(0.85);
    opacity: 0.85;
  }
}

@keyframes floatC {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: translate3d(-120px, 100px, 0) scale(1.6) rotate(180deg);
    opacity: 0.9;
  }
}

/* ============================================
   FONDO ESTÁTICO PARA MÓVIL (SIN ANIMACIONES)
   Reemplaza los círculos animados
   ============================================ */
.static-background {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: radial-gradient(
      circle at 85% 20%,
      rgba(0, 255, 255, 0.25) 0%,
      transparent 35%
    ),
    radial-gradient(
      circle at 75% 70%,
      rgba(0, 230, 240, 0.2) 0%,
      transparent 30%
    ),
    radial-gradient(
      circle at 60% 40%,
      rgba(0, 200, 220, 0.15) 0%,
      transparent 25%
    );
}

/* ============================================
   START TEST BUTTON
   ============================================ */
.start-test-btn {
  padding: 0 32px;
  min-height: 52px;
  transition: all var(--transition-bounce);
  box-shadow: 0 4px 14px var(--color-cta-shadow), 0 2px 6px rgba(0, 0, 0, 0.2);
  background: linear-gradient(135deg, var(--color-cta) 0%, var(--color-cta-dark) 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  overflow: hidden;
}

.start-test-btn .button-text {
  letter-spacing: 0.025em;
}

.start-test-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.start-test-btn:hover::before {
  left: 100%;
}

.start-test-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 25px rgba(200, 61, 45, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, var(--color-cta-light) 0%, var(--color-cta) 100%) !important;
}

.start-test-btn:active {
  transform: translateY(-1px) scale(1);
  box-shadow: 0 4px 14px var(--color-cta-shadow), 0 2px 6px rgba(0, 0, 0, 0.2);
}

.start-test-btn:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.5);
  outline-offset: 3px;
}

.start-test-btn.mobile-btn {
  margin-top: 16px;
  padding: 0 24px;
  min-height: 48px;
  font-size: 0.9375rem;
}

.start-test-btn.mobile-btn :deep(.v-btn__content) {
  justify-content: center;
  gap: 6px;
}

/* ============================================
   DOWNLOAD CARD
   ============================================ */
.download-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  /* CLS fix: altura fluida para evitar saltos entre breakpoints */
  min-height: clamp(240px, 20vw + 180px, 280px);
  contain: layout style;
}

.store-download-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  /* CLS fix: altura fluida */
  min-height: clamp(140px, 12vw + 100px, 170px);
}

.qr-wrapper {
  background: #fafafa;
  padding: clamp(6px, 0.5vw + 4px, 8px);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  /* CLS fix: tamaño fluido para evitar saltos */
  width: clamp(112px, 11vw + 70px, 156px);
  height: clamp(112px, 11vw + 70px, 156px);
  display: flex;
  align-items: center;
  justify-content: center;
  contain: layout style;
}

.qr-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.qr-image {
  /* CLS fix: tamaño fluido */
  width: clamp(100px, 10vw + 60px, 140px);
  height: clamp(100px, 10vw + 60px, 140px);
  display: block;
  aspect-ratio: 1 / 1;
  object-fit: contain;
}

.store-badge-download {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, filter 0.2s ease;
  border-radius: 6px;
  overflow: hidden;
}

.google-play-badge {
  /* CLS fix: tamaño fluido para evitar saltos */
  height: clamp(42px, 3vw + 30px, 50px);
  width: clamp(109px, 8vw + 80px, 130px);
  min-height: clamp(42px, 3vw + 30px, 50px);
  min-width: clamp(109px, 8vw + 80px, 130px);
  object-fit: contain;
}
.app-store-badge {
  /* CLS fix: tamaño fluido para evitar saltos */
  height: clamp(32px, 2.5vw + 22px, 40px);
  width: clamp(96px, 7vw + 70px, 120px);
  min-height: clamp(32px, 2.5vw + 22px, 40px);
  min-width: clamp(96px, 7vw + 70px, 120px);
  object-fit: contain;
}

.store-badge-download:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
}
.store-badge-download:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.badge-image {
  height: 100%;
  width: 100%;
  object-fit: contain;
  display: block;
}


/* ============================================
   STATS HERO (FRANJA OSCURA)
   ============================================ */
.stats-hero {
  position: relative;
  margin: 16px 0 8px;
  border-radius: 14px;
  overflow: hidden;
}

.stat-skeleton {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 25%, rgba(255, 255, 255, 0.18) 50%, rgba(255, 255, 255, 0.08) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

.stat-skeleton--number {
  width: 90px;
  height: 28px;
}

.stat-skeleton--label {
  width: 120px;
  height: 12px;
  margin-top: 4px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.stats-hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a2f3d 0%, #2f4858 40%, #3d5a6e 100%);
}

.stats-hero-bg::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 20% 50%, rgba(25, 118, 210, 0.15) 0%, transparent 70%),
              radial-gradient(ellipse at 80% 50%, rgba(200, 61, 45, 0.1) 0%, transparent 70%);
}

.stats-hero-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 20px 28px;
  transition: opacity 0.3s ease;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex: 1;
  text-align: center;
}

.stat-big-number {
  font-size: 1.75rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  letter-spacing: -0.03em;
  min-width: 4ch;
  display: inline-block;
}

.stat-big-pct {
  font-size: 1.4rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
}

.stat-big-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.stat-sub {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 400;
  letter-spacing: 0.02em;
}

.stat-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
  margin: 0 8px;
}

@media (max-width: 600px) {
  .stats-hero-content {
    padding: 16px 12px;
    gap: 0;
  }

  .stat-big-number {
    font-size: 1.25rem;
  }

  .stat-big-label {
    font-size: 0.63rem;
  }

  .stat-dot {
    width: 3px;
    height: 3px;
  }
}

@media (min-width: 960px) {
  .stat-big-number {
    font-size: 2.1rem;
  }

  .stats-hero-content {
    padding: 24px 40px;
  }
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  /* CLS fix: altura fluida */
  min-height: clamp(100px, 8vw + 70px, 120px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(10, 25, 41, 0.02) 0%,
    rgba(0, 30, 60, 0.03) 100%
  );
  opacity: 1;
  transition: opacity 0.3s ease;
  z-index: 0;
}

.section-card:hover::before {
  opacity: 0;
}

.section-card-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.04) 0%,
    rgba(var(--v-theme-primary), 0.03) 50%,
    rgba(var(--v-theme-primary), 0.04) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.section-card:hover .section-card-gradient {
  opacity: 1;
}
.section-card-content {
  position: relative;
  z-index: 2;
}

.section-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  background: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.section-icon {
  color: rgb(var(--v-theme-primary));
  transition: all 0.3s ease;
}

.section-card:hover .section-icon-wrapper {
  background: rgba(var(--v-theme-primary), 0.18);
  border-color: rgba(var(--v-theme-primary), 0.4);
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.25);
}

.section-card:hover .section-icon {
  color: rgb(var(--v-theme-primary));
  transform: scale(1.05);
}

.section-title {
  color: rgba(10, 25, 41, 0.95);
  font-weight: 600;
  transition: all 0.3s ease;
  line-height: 1.2;
}
.section-card:hover .section-title {
  color: rgba(10, 25, 41, 1);
}

.section-subtitle {
  color: rgba(10, 25, 41, 0.7);
  line-height: 1.5;
  transition: color 0.3s ease;
}
.section-card:hover .section-subtitle {
  color: rgba(10, 25, 41, 0.85);
}

.section-arrow {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.08);
  border: 1px solid rgba(var(--v-theme-primary), 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateX(-8px);
  transition: all 0.3s ease;
}

.section-card:hover .section-arrow {
  opacity: 1;
  transform: translateX(0);
  background: rgba(var(--v-theme-primary), 0.12);
  border-color: rgba(var(--v-theme-primary), 0.25);
}

.section-arrow .v-icon {
  color: rgba(var(--v-theme-primary), 0.7);
  transition: all 0.3s ease;
}
.section-card:hover .section-arrow .v-icon {
  color: rgb(var(--v-theme-primary));
  transform: translateX(3px);
}

.section-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.25);
  box-shadow:
    0 8px 25px rgba(var(--v-theme-primary), 0.15),
    0 4px 10px rgba(0, 0, 0, 0.06);
}

.section-card:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.5);
  outline-offset: 3px;
  border-color: rgba(var(--v-theme-primary), 0.3);
}

/* ============================================
   APP ICON HERO
   ============================================ */

/* ============================================
   MODAL
   ============================================ */
.mobile-dialog-qr {
  overflow: hidden;
}

.logo-wrapper-modal {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  min-height: 38px;
}
.cnmc-logo-modal {
  max-width: 200px;
  width: 100%;
  height: auto;
  object-fit: contain;
  aspect-ratio: 502 / 56;
}

.qr-modal-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-modal-wrapper {
  background: #fafafa;
  padding: 10px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-modal-image {
  width: 120px;
  height: 120px;
  display: block;
  aspect-ratio: 1 / 1;
  object-fit: contain;
}

.modal-badge-single {
  display: flex;
  justify-content: center;
  min-height: 50px;
}

.modal-store-badge {
  display: inline-block;
  transition: all 0.2s ease;
  border-radius: 8px;
  overflow: hidden;
}

.modal-store-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.modal-store-badge:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 3px;
}

.modal-badge-img {
  display: block;
  width: auto;
  height: 50px;
  object-fit: contain;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (min-width: 1280px) {
  .qr-image {
    width: 150px;
    height: 150px;
  }
  .qr-wrapper {
    width: 168px;
    height: 168px;
  }
  .google-play-badge {
    height: 56px;
    width: 145px;
    min-height: 56px;
    min-width: 145px;
  }
  .app-store-badge {
    height: 44px;
    width: 132px;
    min-height: 44px;
    min-width: 132px;
  }
  .store-download-item {
    min-height: 190px;
  }
}

@media (min-width: 960px) {
  .hero-title {
    font-size: 2.25rem !important;
  }
  .hero-subtitle {
    font-size: 1.1rem !important;
    line-height: 1.7;
  }
  .start-test-btn {
    padding: 0 36px;
    min-height: 56px;
    font-size: 1rem;
  }
}

@media (min-width: 960px) and (max-width: 1279px) {
  .qr-image {
    width: 90px;
    height: 90px;
  }
  .qr-wrapper {
    width: 106px;
    height: 106px;
  }
  .google-play-badge {
    height: 46px;
    width: 120px;
    min-height: 46px;
    min-width: 120px;
  }
  .app-store-badge {
    height: 36px;
    width: 108px;
    min-height: 36px;
    min-width: 108px;
  }
  .store-download-item {
    min-height: 160px;
  }
}

@media (max-width: 1279px) {
  .download-card {
    margin-top: 8px;
    margin-bottom: 8px;
  }
}

@media (max-width: 960px) {
  /* CLS fix: min-height ahora usa clamp() en la clase base */
  .download-card {
    margin-top: 0;
    margin-bottom: 8px;
  }
  .animation-toggle-btn {
    top: 6px;
    right: 6px;
  }

  /* En móvil: sin transiciones en hover para mejor rendimiento */
  .start-test-btn:hover {
    transform: none;
  }
  .section-card:hover {
    transform: none;
  }
  .qr-wrapper:hover {
    transform: none;
  }
  .section-arrow {
    opacity: 0.6;
    transform: none;
  }
}

@media (max-width: 600px) {
  /* CLS fix: tamaños principales ahora usan clamp() en clases base */
  .hero-subtitle {
    font-size: 0.875rem !important;
  }
  .qr-wrapper {
    border-radius: 8px;
  }
  .download-card {
    margin-bottom: 16px;
  }
  .section-icon-wrapper {
    width: 38px;
    height: 38px;
  }
  .section-arrow {
    width: 30px;
    height: 30px;
    bottom: 8px;
    right: 8px;
  }
  .qr-modal-image {
    width: 110px;
    height: 110px;
  }
  .qr-modal-wrapper {
    width: 130px;
    height: 130px;
  }
  .modal-badge-img {
    height: 44px;
  }
  .cnmc-logo-modal {
    max-width: 180px;
  }
  .logo-wrapper-modal {
    min-height: 34px;
  }
  .start-test-btn.mobile-btn {
    padding: 0 20px;
    min-height: 46px;
    font-size: 0.875rem;
  }
}

@media (max-width: 360px) {
  .qr-modal-image {
    width: 100px;
    height: 100px;
  }
  .qr-modal-wrapper {
    width: 120px;
    height: 120px;
  }
  .modal-badge-img {
    height: 40px;
  }
  .cnmc-logo-modal {
    max-width: 150px;
  }
  .logo-wrapper-modal {
    min-height: 30px;
  }
  .start-test-btn.mobile-btn {
    padding: 0 16px;
    min-height: 44px;
    font-size: 0.8125rem;
  }
}

/* ============================================
   ACCESIBILIDAD: prefers-reduced-motion
   ============================================ */
@media (prefers-reduced-motion: reduce) {
  .circle {
    animation: none !important;
    opacity: 0.7;
  }

  .start-test-btn,
  .section-card,
  .qr-wrapper,
  .store-badge-download,
  .modal-store-badge,
  .animation-toggle-btn,
  .section-icon-wrapper,
  .section-icon,
  .section-arrow,
  .section-card-gradient {
    transition: none !important;
  }

  .start-test-btn::before {
    display: none;
  }

  .start-test-btn:hover,
  .section-card:hover,
  .qr-wrapper:hover,
  .store-badge-download:hover,
  .modal-store-badge:hover {
    transform: none !important;
  }

  .section-card:hover .section-icon-wrapper,
  .section-card:hover .section-icon {
    transform: none !important;
  }

  .section-arrow {
    opacity: 1 !important;
    transform: none !important;
  }

  .section-card:hover .section-arrow .v-icon {
    transform: none !important;
  }
}

/* ============================================
   ACCESIBILIDAD: prefers-contrast (alto contraste)
   ============================================ */
@media (prefers-contrast: high) {
  .section-card,
  .download-card,
  .qr-wrapper {
    border: 2px solid rgba(0, 0, 0, 0.3);
  }
  .section-icon-wrapper {
    border: 2px solid currentColor;
  }
  .animation-toggle-btn {
    border: 2px solid rgba(255, 255, 255, 0.4);
  }
  .start-test-btn {
    border: 2px solid rgba(255, 255, 255, 0.3);
  }
  .section-card:focus-visible,
  .start-test-btn:focus-visible {
    outline-width: 3px;
  }
}
</style>